import "@material/web/button/text-button";

import { AsyncPipe, CurrencyPipe, DecimalPipe } from "@angular/common";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MatDialogModule } from "@angular/material/dialog";
import { map, Observable } from "rxjs";

import { NodDatePickerComponent } from "../nod-date-picker/nod-date-picker.component";
import { DateFormatPipe } from "../pipes/date-format-pipe";
import {
  QPilotScheduledOrder,
  QPilotScheduledOrderTotals,
} from "../types/scheduled-order";
import { NodPickerConfig, QPilotSiteSettings } from "./../types/site";
import { provideNativeDateAdapter } from "@angular/material/core";

@Component({
  selector: "lib-resume-dialog",
  standalone: true,
  imports: [
    DecimalPipe,
    CurrencyPipe,
    MatDialogModule,
    AsyncPipe,
    DateFormatPipe,
    NodDatePickerComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./resume-dialog.component.html",
  styleUrl: "./resume-dialog.component.scss",
  providers: [provideNativeDateAdapter()],
})
export class ResumeDialogComponent implements OnInit, OnChanges {
  @Input() order: QPilotScheduledOrder;
  @Input() orderTotals$: Observable<QPilotScheduledOrderTotals>;
  @Input() orderVerboseName: string;
  @Input() siteSettings: QPilotSiteSettings;

  @Output() resumeOrder = new EventEmitter();
  @Output() processNow = new EventEmitter();
  @Output() updateNOD = new EventEmitter<{ dateValid: boolean; date: Date }>();
  @Output() rescheduleAndResume = new EventEmitter<{
    orderId: number;
    nextOccurrenceUtc: Date;
  }>();

  savings$: Observable<{ value: number }>;
  isNextOccurrenceInPast: boolean = false;
  selectedDate: Date | null = null;
  isResumeEnabled: boolean = false;
  nodPickerConfig: NodPickerConfig;
  date: Date;

  constructor() {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.["order"]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const order = changes?.["order"].currentValue;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const oldOrder = changes?.["order"]?.previousValue;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (order.nextOccurrenceUtc !== oldOrder?.nextOccurrenceUtc) {
        this.isNextOccurrenceInPast = !this.isNextOccurrenceInPast;
      }
    }
  }

  ngOnInit() {
    this.savings$ = this.orderTotals$.pipe(
      map((totals) => ({
        value: totals.itemsTotals.subTotal - totals.itemsTotals.total,
      })),
    );

    // Check if NextOccurrenceUtc is in the past
    const nextOccurrence = new Date(this.order.nextOccurrenceUtc);
    const today = new Date();
    this.isNextOccurrenceInPast = nextOccurrence < today;

    // If NextOccurrenceUtc is in the future, enable the Resume button
    this.isResumeEnabled = !this.isNextOccurrenceInPast;

    // Set up the date picker configuration
    const utcOffset = this.siteSettings.utcOffset ?? 0;
    this.date = this.getDateFromUtcOffsetForDisplay(nextOccurrence, utcOffset);

    this.nodPickerConfig = {
      lockedDays: this.siteSettings.lockWindow,
      supportsProcessingWindow: this.siteSettings.supportsProcessingWindow,
      processingStartTime: this.siteSettings.orderProcessingStartTime,
      processingEndTime: this.siteSettings.orderProcessingEndTime,
      siteProcessingOffset: this.siteSettings.orderProcessingOffset,
      offset: utcOffset,
      scheduledOrderProcessingOffset: this.siteSettings.orderProcessingOffset,
    };
  }
  private getDateFromUtcOffsetForDisplay(date: Date, offset: number): Date {
    const adjustedDate = new Date(date.getTime());
    offset += new Date().getTimezoneOffset() / 60;
    adjustedDate.setTime(adjustedDate.getTime() + offset * 60 * 60 * 1000);
    return adjustedDate;
  }
  onResumeOrder() {
    this.resumeOrder.emit();
  }

  onProcessNow() {
    this.processNow.emit();
  }

  onDateChanged(date: Date | null) {
    this.selectedDate = date;
    // Enable the Resume button if a valid date is selected

    this.isResumeEnabled = !!date;
    if (this.isResumeEnabled) {
      this.updateNOD.emit({
        dateValid: this.isResumeEnabled,
        date: this.selectedDate!,
      });
    }
  }
}
